import { createBombRainSpectator } from '@features/game-spectator/bomb-rain-spectator'
import { createBossArenaSpectator } from '@features/game-spectator/boss-arena-spectator'
import { createCrowdCanvasSpectator } from '@features/game-spectator/crowd-canvas-spectator'
import { useGameSpectator } from '@features/game-spectator/useGameSpectator'
import {
  isBombRainState,
  isBossArenaState,
  isCrowdCanvasState,
} from '@shared/games/protocol'
import { useCallback, useEffect, useRef, useState } from 'react'

interface Props {
  channelId: string
  enabled?: boolean
}

type Mode = 'bomb_rain' | 'crowd_canvas' | 'boss_arena' | null

export function GamesLayer({ channelId, enabled = true }: Props) {
  const hostNodeRef = useRef<HTMLDivElement | null>(null)
  const modeRef = useRef<Mode>(null)
  const handleRef = useRef<{
    applyState: (state: unknown) => void
    clear: () => void
    destroy: () => void
  } | null>(null)
  const [state, setState] = useState<unknown>(null)
  // Флаг аварийного слоя: выставляется, если текущая Phaser-сцена упала с ошибкой.
  // Ничего не рендерим вместо сломанного компонента, остальной оверлей продолжает работу.
  const [layerBroken, setLayerBroken] = useState(false)
  const brokenModeRef = useRef<Mode>(null)

  useGameSpectator({
    channelId,
    enabled,
    onState: setState,
  })

  const destroyHandle = useCallback(() => {
    try {
      handleRef.current?.destroy()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[overlay/games] error destroying game spectator:', error)
    }
    handleRef.current = null
    modeRef.current = null
  }, [])

  // Единая точка отката при сбое любой игры: логируем, уничтожаем сломанную
  // сцену и прячем слой до появления состояния другого режима/игры — остальной оверлей
  // (эффекты, чат, и т.д.) продолжает работать как ни в чём.
  const handleLayerError = useCallback(
    (mode: Mode, error: unknown) => {
      // eslint-disable-next-line no-console
      console.error(`[overlay/games] "${mode}" spectator crashed, hiding layer:`, error)
      brokenModeRef.current = mode
      setLayerBroken(true)
      destroyHandle()
    },
    [destroyHandle],
  )

  const ensureMode = useCallback(
    (mode: Mode) => {
      const node = hostNodeRef.current
      if (!node || !mode) return null
      if (modeRef.current === mode && handleRef.current) return handleRef.current
      destroyHandle()
      try {
        handleRef.current =
          mode === 'bomb_rain'
            ? createBombRainSpectator(node)
            : mode === 'crowd_canvas'
              ? createCrowdCanvasSpectator(node)
              : createBossArenaSpectator(node)
        modeRef.current = mode
      } catch (error) {
        handleLayerError(mode, error)
        return null
      }
      return handleRef.current
    },
    [destroyHandle, handleLayerError],
  )

  const hostRef = useCallback(
    (node: HTMLDivElement | null) => {
      hostNodeRef.current = node
      if (!node) destroyHandle()
    },
    [destroyHandle],
  )

  // Безопасно вызывает applyState на текущем handle: любое исключение внутри
  // Phaser-сцены (напр., невалидные данные с сервера) ловится здесь и не роняет
  // весь React-дерево оверлея (ErrorBoundary тут не сработает, т.к. вызов идёт из useEffect,
  // а не из рендера).
  const safeApplyState = useCallback(
    (mode: Mode, s: unknown) => {
      const handle = ensureMode(mode)
      if (!handle) return
      try {
        handle.applyState(s)
      } catch (error) {
        handleLayerError(mode, error)
      }
    },
    [ensureMode, handleLayerError],
  )

  useEffect(() => {
    if (!state) {
      handleRef.current?.clear()
      return
    }
    if (isBombRainState(state)) {
      if (layerBroken && brokenModeRef.current === 'bomb_rain') return
      setLayerBroken(false)
      safeApplyState('bomb_rain', state)
      return
    }
    if (isCrowdCanvasState(state)) {
      if (layerBroken && brokenModeRef.current === 'crowd_canvas') return
      setLayerBroken(false)
      safeApplyState('crowd_canvas', state)
      return
    }
    if (isBossArenaState(state)) {
      if (layerBroken && brokenModeRef.current === 'boss_arena') return
      setLayerBroken(false)
      safeApplyState('boss_arena', state)
      return
    }
    handleRef.current?.clear()
  }, [state, layerBroken, safeApplyState])

  const visible =
    !layerBroken &&
    (isBombRainState(state) || isCrowdCanvasState(state) || isBossArenaState(state))

  return (
    <div
      ref={hostRef}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        visibility: visible ? 'visible' : 'hidden',
      }}
      aria-hidden
    />
  )
}
